import classNames from "classnames"
import ApplicationComponent from "legacy/ApplicationComponent"
import DatePicker from "legacy/DatePicker"
import FormGroup from "legacy/FormGroup"
import LegacyButton from "legacy/LegacyButton"
import ContractGroupV2PanelPage from "legacy/contract-groups-v2/ContractGroupV2PanelPage"
import { validatorFactory } from "legacy/validatorFactory"
import _ from "lodash"
import moment from "moment"
import PropTypes from "prop-types"
import React from "react"

// Note: $context is loaded globally
// Note: Amplitude is loaded globally

const DUE_DATE_VALUES = [
  ...[...Array(28).keys()].map((i) => ({
    value: i + 1,
    display: i + 1,
  })),
  {
    value: 32,
    display: "End of month",
  },
]

class ContractGroupV2Itinerary extends ApplicationComponent {
  constructor(props) {
    super(props)
    this.validator = validatorFactory({ autoForceUpdate: this })
    const contractType = props.form.contractType || "month_to_month"
    this.state = {
      name: props.form.name || "",
      spaceType: props.form.spaceType || ($context.mbmEnabled ? "" : "mooring"),
      contractType,
      startDate: props.form.startDate,
      endDate: props.form.endDate,
      billingCycle:
        props.form.billingCycle ||
        ($context.mbmEnabled ? "month_to_month" : "first_of_month"),
      dueDay:
        props.form.dueDay ||
        (contractType === "month_to_month" ? 1 : undefined),
      error: false,
    }

    this.next = this.next.bind(this)
    this.selectMonthToMonth = this.selectMonthToMonth.bind(this)
    this.selectFixedPeriod = this.selectFixedPeriod.bind(this)
    this.setStartDate = this.setStartDate.bind(this)
  }

  fetchData() {
    return this.state
  }

  componentDidUpdate() {
    if (!this.state.error && this.props.incomplete) {
      this.setState({ error: true })
      this.validator.showMessages()
    }

    // so this is kinda tricky, but we want to pass back a signal that
    // this initial page has been touched and navigation should be blocked
    if (
      !this.props.form.dontNavigate &&
      (this.state.name !== "" ||
        this.state.spaceType !== "" ||
        this.state.contractType !== "" ||
        this.state.startDate != null ||
        this.state.endDate != null)
    ) {
      this.props.update({ dontNavigate: true })
    }
  }

  componentWillUnmount() {
    Amplitude.trackEvent("contracts_v2:itinerary_complete")
    this.props.update(this.state)
  }

  next() {
    this.props.route.goNext()
  }

  startMoment() {
    return this.state.startDate && moment(this.state.startDate)
  }

  endMoment() {
    return this.state.endDate && moment(this.state.endDate)
  }

  // Add one year to start date to determine end date for month-to-month contracts
  monthToMonthEndDate(startDate) {
    return moment(startDate).add(1, "y").format("YYYY-MM-DD")
  }

  selectMonthToMonth() {
    this.setState(
      {
        contractType: "month_to_month",
        billingCycle: $context.mbmEnabled ? "month_to_month" : "first_of_month",
        endDate: this.monthToMonthEndDate(this.state.startDate),
      },
      () => this.props.update(this.fetchData())
    )
  }

  selectFixedPeriod() {
    const endDate =
      _.isEmpty(this.props.form.endDate) && !this.props.isGroup
        ? $context.form.endDate
        : this.props.form.endDate
    this.setState(
      {
        contractType: "standard",
        billingCycle: "first_of_month",
        endDate,
      },
      () => this.props.update(this.fetchData())
    )
  }

  setStartDate(startDate) {
    const newState = {}
    newState.startDate = moment(startDate).format("YYYY-MM-DD")

    if (this.state.contractType === "month_to_month") {
      newState.endDate = this.monthToMonthEndDate(startDate)
    }

    this.setState(newState)
  }

  render() {
    return (
      <ContractGroupV2PanelPage
        hasNext={this.props.route.hasNext}
        hasPrevious={this.props.route.hasPrevious}
        goNext={this.next}
        goPrevious={this.props.route.goPrevious}
        saving={this.props.saving}
      >
        {this.props.isGroup && (
          <FormGroup
            required
            label="Name"
            name="name"
            value={this.state.name}
            onChange={this.handleInputChange}
            validation={this.validator.message(
              "name",
              this.state.name,
              "required"
            )}
          />
        )}
        {$context.mbmEnabled ? null : (
          <div className="form-group">
            <div>
              <label htmlFor="space-type">Space Type</label>
            </div>
            <div id="space-type" className="space-x-2">
              <LegacyButton
                type={this.state.spaceType === "mooring" ? "primary" : ""}
                onClick={() => this.setState({ spaceType: "mooring" })}
              >
                Mooring
              </LegacyButton>
              <LegacyButton
                type={this.state.spaceType === "dock" ? "primary" : ""}
                className={classNames()}
                onClick={() => this.setState({ spaceType: "dock" })}
              >
                Dock
              </LegacyButton>
              <LegacyButton
                type={this.state.spaceType === "storage" ? "primary" : ""}
                onClick={() => this.setState({ spaceType: "storage" })}
              >
                Storage
              </LegacyButton>
            </div>
          </div>
        )}

        <div className="form-group">
          <div>
            <label htmlFor="contract-type">Contract Length</label>
          </div>
          <div id="contract-type" className="space-x-2">
            <LegacyButton
              type={
                this.state.contractType === "month_to_month" ? "primary" : ""
              }
              onClick={this.selectMonthToMonth}
              disabled={this.props.monthlyDisabled}
            >
              Month to Month
            </LegacyButton>
            <LegacyButton
              type={this.state.contractType === "standard" ? "primary" : ""}
              onClick={this.selectFixedPeriod}
            >
              Fixed Period
            </LegacyButton>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="form-group grid-span-1">
            <label htmlFor="select-start-date">Start date</label>
            <span className="text-danger bs-ml-1">*</span>
            <div id="select-start-date" className="form-control-with-icon">
              <i className="fa-kit fa-calendar form-control-icon-left" />
              <DatePicker
                setParentState={this.setStartDate}
                value={
                  this.state.startDate &&
                  moment(this.state.startDate).format("L")
                }
                datesDisabled={[]}
                clearBtn={false}
              />
            </div>
            {this.validator.message(
              "startDate",
              this.state.startDate,
              "required"
            )}
          </div>
          {this.state.contractType !== "month_to_month" &&
            this.state.contractType !== "" && (
              <div className="form-group grid-span-1">
                <label htmlFor="select-end-date">End date</label>
                <span className="text-danger bs-ml-1">*</span>
                <div id="select-end-date" className="form-control-with-icon">
                  <i className="fa-kit fa-calendar form-control-icon-left" />
                  <DatePicker
                    setParentState={(endDate) =>
                      this.setState({
                        endDate: moment(endDate).format("YYYY-MM-DD"),
                      })
                    }
                    value={
                      this.state.endDate &&
                      moment(this.state.endDate).format("L")
                    }
                    datesDisabled={[]}
                    clearBtn={false}
                    startDate={moment(this.state.startDate).format("L")}
                  />
                </div>
                {this.state.contractType === "standard" &&
                  this.validator.message("endDate", this.endMoment(), [
                    "required",
                    { after: this.startMoment() },
                  ])}
              </div>
            )}
        </div>
        {this.state.contractType === "month_to_month" && $context.mbmEnabled ? (
          <div className="grid grid-cols-2">
            <FormGroup
              required
              name="dueDay"
              label="Monthly due day"
              inputType="select"
              value={this.state.dueDay}
              onChange={this.handleInputChange}
              validation={this.validator.message(
                "dueDay",
                this.state.dueDay,
                "required"
              )}
            >
              {DUE_DATE_VALUES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.display}
                </option>
              ))}
            </FormGroup>
          </div>
        ) : null}
      </ContractGroupV2PanelPage>
    )
  }
}

ContractGroupV2Itinerary.propTypes = {
  form: PropTypes.shape({
    name: PropTypes.string,
    spaceType: PropTypes.string,
    contractType: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    billingCycle: PropTypes.string,
    dueDay: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dontNavigate: PropTypes.bool,
  }).isRequired,
  update: PropTypes.func.isRequired,
  route: PropTypes.shape({
    hasNext: PropTypes.bool,
    hasPrevious: PropTypes.bool,
    goNext: PropTypes.func,
    goPrevious: PropTypes.func,
  }).isRequired,
  saving: PropTypes.bool,
  incomplete: PropTypes.bool,
  isGroup: PropTypes.bool,
  monthlyDisabled: PropTypes.bool,
}

export default ContractGroupV2Itinerary
